/**
 * The client half of the phase 3d adopt surface .
 *
 * One query and three mutations, all under `settings.warehouses`. Every mutation invalidates the
 * whole namespace rather than a hand-picked key list: an adopt moves a link, which changes the
 * account overview, the connection list AND the buckets view, and a partial invalidation here is how
 * two panels on one screen come to disagree about which account a connection uses.
 */
import type { WarehouseProblem } from '@server/warehouse-failure';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import { refreshPermissionOnRefusal } from '../accountCreatePermission';
import { refusalMessage } from '../refusalCopy';
import { listRequestFailed, listUnavailable } from '../shared/testRequestFailed';
import { toastAdopted } from './adoptedToast';

/** One row of `listRsAccountsOverview`, as the screen needs it. Mirrors the server type. */
export interface RsAccountOverview {
  accountId: string;
  name: string | null;
  type: string;
  host: string | null;
  database: string | null;
  lookoutOwned: boolean;
  /**
   * The name is still the one the mirror minted (`lookoutAccountName`), untouched by a human.
   *
   * Deliberately NOT `lookoutOwned`: that mark is also written by the platform create form, where the
   * customer types the name themselves, and a minted account can be renamed in RudderStack afterwards.
   * Only this answers "is the name ours to replace?".
   */
  nameIsMinted: boolean;
  /**
   * `paused` is a FAULT the admin has to act on — the holding connection's account is gone in
   * RudderStack (ACT2-877 decision 3). A workspace merely waiting on the rollout flag stays
   * `opted_in`, because nobody here can clear that.
   */
  state: 'usable_by_agent' | 'opted_in' | 'paused' | 'not_opted_in';
  /** Which fault, when `state` is `paused`. Null otherwise. */
  pausedReason: string | null;
  connectionId: string | null;
  connectionKey: string | null;
  adopted: boolean;
  /**
   * Would Connect on this row be accepted? Answered per ACCOUNT by the server, against this
   * account's own credential family.
   *
   * `adoptionEnabled` below is the workspace half and is true when ANY family is ramped, so it
   * cannot gate a row: a Postgres-ramped workspace holding a BigQuery login rendered an enabled
   * Connect that the server then refused. Rows gate on this; the DRAWER, which adopts onto a
   * connection the caller already chose, still gates on `adoptionEnabled`.
   */
  connectable: boolean;
  usageCount: number | null;
}

export interface AdoptAccountsHook {
  accounts: RsAccountOverview[];
  isLoading: boolean;
  /** The platform list could not be read; `accounts` is empty rather than wrong. */
  unavailable?: string;
  /**
   * Why the list could not be read, in words (ACT2-1077): "renew the RudderStack link" and "RudderStack
   * is down" have different next steps. Also set when the overview request itself failed.
   */
  unavailableProblem?: WarehouseProblem;
  /** False when the workspace cannot read credentials from RudderStack yet. */
  adoptionEnabled: boolean;
  adopt: (input: { connectionKey: string; accountId: string; acknowledgeMismatch?: boolean }) => void;
  adoptPending: boolean;
  adoptError: unknown;
  resetAdopt: () => void;
  /**
   * ACT2-877: adopt an account the workspace has no connection for, which CREATES the connection.
   *
   * Takes no connection key — the server derives the connection from the account, so there is nothing
   * for the client to choose and nothing for it to get wrong.
   */
  adoptAsNewConnection: (input: { accountId: string }) => void;
  /** The account id an adopt-as-new is in flight for, or null. Per-row, so one row's spinner is its own. */
  adoptingNewAccountId: string | null;
  adoptAsNewError: unknown;
  /** The account the last adopt-as-new refusal was about, so a row only renders its OWN refusal. */
  adoptAsNewErrorAccountId: string | null;
  resetAdoptAsNew: () => void;
  detach: (input: { connectionKey: string }) => void;
  detachPending: boolean;
}

export function useAdoptAccounts(options?: {
  onAdopted?: () => void;
  /** Fires with the account id once an adopt-as-new succeeds — the chat connect card needs to know which. */
  onAdoptedAsNew?: (accountId: string) => void;
}): AdoptAccountsHook {
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  const overview = useQuery(orpc.settings.warehouses.listRsAccountsOverview.queryOptions());

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
  }, [queryClient, orpc]);

  const adoptMutation = useMutation(
    orpc.settings.warehouses.adoptAccount.mutationOptions({
      onSuccess: (result) => {
        invalidate();
        // "Within a few minutes", never "immediately": the pool, token and options caches are
        // process-local and the deployment is multi-replica, so another replica keeps serving the
        // previous account until its own TTL expires (see `warehouse-connection-eviction.ts`).
        // ACT2-1130: the server dialled the account before binding it; a missing `_rudderstack`
        // grant rides beside the success as a warning.
        toastAdopted(
          result.reAdopt
            ? 'Account changed. Queries will use it within a few minutes.'
            : 'Account adopted. Queries will use it within a few minutes.',
          result,
        );
        options?.onAdopted?.();
      },
      // No toast: a denial is a decision the drawer renders inline, beside the account it is about.
      // A toast would put the reason somewhere the admin has already stopped looking.
      onError: (error: unknown) => refreshPermissionOnRefusal(queryClient, orpc, error),
    }),
  );

  // ACT2-877. `variables` is what makes the per-row rendering possible: the mutation is shared by
  // every row in the list, so without knowing WHICH account it was called for, one row's refusal would
  // render under all of them.
  const adoptAsNewMutation = useMutation(
    orpc.settings.warehouses.adoptAccountAsNewConnection.mutationOptions({
      onSuccess: (data, variables) => {
        invalidate();
        // "Within a few minutes" for the same reason the adopt toast says it — see the note above.
        //
        // "Queries will use it" is a claim about the CONNECTION, and what entitles this toast to make
        // it is the server's own ladder, not the 200: `adoptAccountAsNewConnection` refuses
        // `not_eligible` unless a connection string can actually be built from the account's options,
        // and `read_flag_off` unless the read path would serve that family. This toast used to fire on
        // a BigQuery account with no `location`, whose stored blob no driver could parse (ACT2-877) —
        // the fix is that the server no longer reaches this callback for such an account. If a rung is
        // ever relaxed there, the words here stop being true and have to move with it.
        // ACT2-1130: and the server dialled the account first, refusing `connection_failed` otherwise.
        toastAdopted('Connection created. Queries will use it within a few minutes.', data);
        options?.onAdoptedAsNew?.(variables.accountId);
      },
      // No toast on failure: `connection_exists` names another connection and tells the admin what to
      // do there, which belongs beside the row they clicked, not in a corner of the screen.
      onError: (error: unknown) => refreshPermissionOnRefusal(queryClient, orpc, error),
    }),
  );

  const detachMutation = useMutation(
    orpc.settings.warehouses.detachAccount.mutationOptions({
      onSuccess: () => {
        invalidate();
        toast.success('Account detached. The connection has no credential until you adopt one.');
      },
      onError: (error: unknown) => {
        refreshPermissionOnRefusal(queryClient, orpc, error);
        toast.error(refusalMessage(error, 'Could not detach the account'));
      },
    }),
  );

  const data = overview.data as
    | {
        accounts: RsAccountOverview[];
        unavailable?: string;
        unavailableProblem?: WarehouseProblem;
        adoptionEnabled: boolean;
      }
    | undefined;
  // The overview request itself failing used to render nothing: the list just looked empty. An older
  // server (rolling deploy) sends only `unavailable`, which still has to show the warning.
  const unavailableProblem =
    data?.unavailableProblem ??
    (data?.unavailable === undefined ? undefined : listUnavailable(data.unavailable)) ??
    (overview.error ? listRequestFailed(overview.error) : undefined);

  return {
    accounts: data?.accounts ?? [],
    isLoading: overview.isLoading,
    ...(data?.unavailable === undefined ? {} : { unavailable: data.unavailable }),
    ...(unavailableProblem === undefined ? {} : { unavailableProblem }),
    // Absent while loading reads as "enabled" only after the query resolves; before that the drawer
    // is not open anyway, and defaulting to true avoids a flash of the disabled copy.
    adoptionEnabled: data?.adoptionEnabled ?? true,
    adopt: (input) => adoptMutation.mutate(input),
    adoptPending: adoptMutation.isPending,
    adoptError: adoptMutation.error,
    resetAdopt: () => adoptMutation.reset(),
    adoptAsNewConnection: (input) => adoptAsNewMutation.mutate(input),
    adoptingNewAccountId: adoptAsNewMutation.isPending ? (adoptAsNewMutation.variables?.accountId ?? null) : null,
    adoptAsNewError: adoptAsNewMutation.error,
    adoptAsNewErrorAccountId:
      adoptAsNewMutation.error === null ? null : (adoptAsNewMutation.variables?.accountId ?? null),
    resetAdoptAsNew: () => adoptAsNewMutation.reset(),
    detach: (input) => detachMutation.mutate(input),
    detachPending: detachMutation.isPending,
  };
}
